import ReactMarkdown from "react-markdown";
import { useEffect, useRef, useState } from "react";
import { loadDocIndex } from "@/lib/docLinks";
import { applyDocLinks } from "@/lib/docLinksDom";
import { loadGlossary } from "@/lib/glossary";
import { applyDictionaryAnchors, autoLinkGlossary, detachTooltipDelegation, flashHashEntry } from "@/lib/glossaryDom";
import { useNavigate } from "react-router-dom";

/**
 * Safe lazy loader for remark-gfm.
 *
 * remark-gfm (via mdast-util-gfm-autolink-literal) ships two hostile bits for
 * older WebKit (Safari before 16.4):
 *   1. A regex literal that uses lookbehind + unicode property escapes:
 *        /(?<=^|\s|\p{P}|\p{S})([-.\w+]+)@([-\w]+(?:\.[-\w]+)+)/gu
 *      Older WebKit throws SyntaxError at module init.
 *   2. Even when the module loads, the attacher runs during markdown transform
 *      and constructs / uses the offending regex, which throws inside the
 *      unified pipeline. React catches the throw during render and fires a
 *      react_render_error like "undefined is not an object (evaluating
 *      'n.data')" as the pipeline tears down mid-attach.
 *
 * Two-layer guard:
 *   a) Feature-test lookbehind + Unicode property escapes. If unsupported,
 *      never attempt to load gfm at all.
 *   b) When gfm loads, wrap it in an attacher that swallows any throw from the
 *      plugin. If gfm blows up we degrade to plain markdown instead of a
 *      white-screen error boundary. Note the wrapper preserves `this` binding
 *      via `Function#call`, matching how unified invokes attachers.
 *
 * NOTE: Lookbehind regexes (`(?<=…)`, `(?<!…)`) and named capture groups
 * (`(?<name>…)`) are BANNED in this codebase. See TODOS.md.
 */
function supportsModernRegex(): boolean {
  try {
    // Lookbehind + Unicode property escape probe. If either isn't supported
    // the RegExp constructor throws SyntaxError at call time.
    // eslint-disable-next-line no-new
    new RegExp("(?<=a)b", "u");
    // eslint-disable-next-line no-new
    new RegExp("\\p{P}", "u");
    return true;
  } catch {
    return false;
  }
}

let gfmPluginPromise: Promise<unknown | null> | null = null;
function loadGfmPluginSafe(): Promise<unknown | null> {
  if (!gfmPluginPromise) {
    if (!supportsModernRegex()) {
      gfmPluginPromise = Promise.resolve(null);
      return gfmPluginPromise;
    }
    gfmPluginPromise = import("remark-gfm")
      .then((m) => {
        const inner = (m as { default: unknown }).default as
          | ((this: unknown, opts?: unknown) => unknown)
          | undefined;
        if (typeof inner !== "function") return null;
        // Wrap so a throw inside the plugin can never bubble into React's
        // render pipeline. Preserve `this` binding (unified attacher contract).
        return function safeGfm(this: unknown, opts?: unknown) {
          try {
            return (inner as (this: unknown, opts?: unknown) => unknown).call(this, opts);
          } catch (err) {
            // Best-effort log; do not rethrow.
            // eslint-disable-next-line no-console
            console.warn("[SopMarkdown] remark-gfm attach failed, degrading to plain markdown", err);
            return undefined;
          }
        };
      })
      .catch(() => null);
  }
  return gfmPluginPromise;
}

/** Flatten React children of a code node into plain text. */
function extractText(node: unknown): string {
  if (node == null || node === false || node === true) return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(extractText).join("");
  const el = node as { props?: { children?: unknown } };
  if (el.props) return extractText(el.props.children);
  return "";
}

/**
 * Copyable document-text block. Rendered as <pre> so the post-render glossary
 * and doc-link passes skip it (both reject PRE/CODE subtrees), but styled as
 * serif reading text rather than monospace code.
 */
function SampleBlock({ children }: { children: string }) {
  const [copied, setCopied] = useState(false);
  const text = (children || "").replace(/\n+$/, "");
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); } catch { /* no-op */ }
      document.body.removeChild(ta);
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  };
  return (
    <div className="sample-block">
      <button type="button" onClick={onCopy} className="sample-block-copy" aria-live="polite">
        {copied ? "Copied" : "Copy"}
      </button>
      <pre className="sample-block-body">{text}</pre>
    </div>
  );
}


export function SopMarkdown({
  body,
  docId,
}: {
  body: string;
  docId?: string;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();
  const [gfmPlugin, setGfmPlugin] = useState<unknown | null>(null);
  useEffect(() => {
    let cancelled = false;
    // Wrap in functional updater: React invokes function values passed to
    // setState as updaters (p(prevState)). Storing the plugin directly would
    // execute the remark-gfm attacher with `this === undefined`, throwing
    // "n.data" on every browser and silently disabling gfm. Return the
    // function from an updater so it is stored, not called.
    loadGfmPluginSafe().then((p) => { if (!cancelled) setGfmPlugin(() => p); });
    return () => { cancelled = true; };
  }, []);

  // Defensively strip a single leading H1 from the body. Page-level chrome
  // owns the title h1; the body must never render its own top-level heading.
  let content = (body || "").replace(/^#\s[^\n]*\n+/, "");


  // Auto-append dictionary pointer in a Resources section on every doc page
  // except the dictionary itself. Skip if the body already links to it.
  const isDictionary = docId === "FAQ-000";

  // Display-layer alphabetization for the dictionary: within each H2 section,
  // sort term-entry paragraphs (those beginning with a bold term) by term text,
  // case-insensitive. Non-entry blocks (intros, notes) keep their position at
  // the top of the section. Underlying markdown is unchanged.
  if (isDictionary) {
    content = alphabetizeDictionarySections(content);
  }
  const alreadyLinksDictionary =
    /\]\((?:\/help\/dictionary|\/support\/library\/dictionary)[)#]/.test(content) ||
    /href=["'](?:\/help\/dictionary|\/support\/library\/dictionary)/.test(content) ||
    /see\s+the\s+steward'?s\s+dictionary/i.test(content);
  if (docId && !isDictionary && !alreadyLinksDictionary) {
    const dictLine =
      "Words in this doc you don't recognize: see [The Steward's Dictionary](/support/library/dictionary).";
    const resourcesRe = /(^|\n)(#{1,6})\s+Resources\s*\n/i;
    const m = content.match(resourcesRe);
    if (m) {
      const headingLevel = m[2].length;
      const startIdx = (m.index ?? 0) + m[0].length;
      const rest = content.slice(startIdx);
      const nextHeadingRe = new RegExp(`\\n#{1,${headingLevel}}\\s+`, "");
      const nm = rest.match(nextHeadingRe);
      const endIdx = nm ? startIdx + (nm.index ?? 0) : content.length;
      const section = content.slice(startIdx, endIdx).replace(/\s+$/, "");
      const hasList = /(^|\n)[-*]\s+/.test(section);
      const insertion = hasList ? `\n- ${dictLine}\n` : `\n\n${dictLine}\n`;
      content =
        content.slice(0, startIdx) +
        section +
        insertion +
        (endIdx < content.length ? "\n" + content.slice(endIdx).replace(/^\n+/, "") : "");
    } else {
      content = content.replace(/\s+$/, "") + `\n\n## Resources\n\n${dictLine}\n`;
    }
  }

  // Post-render pipeline: doc-link resolver (raw IDs + "the X doc" phrases),
  // then dictionary linker. Dictionary skips <a> tags so it never nests inside
  // a doc link.
  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;
    let cancelled = false;
    Promise.all([loadDocIndex(), loadGlossary()]).then(([index, entries]) => {
      if (cancelled || !root) return;
      applyDocLinks(root, index, docId);
      if (docId === "FAQ-000") {
        applyDictionaryAnchors(root, entries);
        requestAnimationFrame(() => flashHashEntry(root));
      } else if (docId) {
        autoLinkGlossary(root, entries);
      }
    });
    return () => {
      cancelled = true;
      // Tear the mutation layer down completely so nothing survives into the
      // next document: no stray tooltip in <body>, no bound listeners.
      detachTooltipDelegation(root);
    };
  }, [docId, content]);


  // Client-side navigation for the doc-links we inject after paint.
  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;
    const handler = (e: MouseEvent) => {
      const a = (e.target as HTMLElement).closest("a.doc-link") as HTMLAnchorElement | null;
      if (!a) return;
      const href = a.getAttribute("href") || "";
      if (!href.startsWith("/")) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
      e.preventDefault();
      navigate(href);
    };
    root.addEventListener("click", handler);
    return () => root.removeEventListener("click", handler);
  }, [navigate]);

  return (
    <div ref={containerRef} className="grt-article font-serif" key={docId || "doc"}>
      <ReactMarkdown
        remarkPlugins={gfmPlugin ? [gfmPlugin as never] : []}
        components={{
          pre: ({ children, ...props }) => {
            const child: any = Array.isArray(children) ? children[0] : children;
            const cls: string = child?.props?.className || "";
            if (/(^|\s)language-sample(\s|$)/.test(cls)) {
              return <SampleBlock>{extractText(child?.props?.children)}</SampleBlock>;
            }
            return <pre {...props}>{children}</pre>;
          },
          a: ({ href, children }) => {
            const isExternal = !!href && /^https?:\/\//i.test(href);
            return isExternal ? (
              <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>
            ) : (
              <a href={href}>{children}</a>
            );
          },
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}

/**
 * Alphabetize dictionary term entries within each H2 section.
 *
 * A "term entry" is a paragraph block (separated by blank lines) whose first
 * non-whitespace characters are a bold span (`**Term.**` or `**Term**`). The
 * sort key is the bold text with any trailing period stripped, lowercased.
 * Non-entry blocks (paragraphs, lists, notes that don't start with bold) keep
 * their original order and stay above the sorted entries within that section.
 *
 * Section boundaries: H2 headings (`## `). Content before the first H2 and any
 * non-H2 headings (H1, H3+) do not partition; entries between two H2s form one
 * sortable group.
 */
function alphabetizeDictionarySections(md: string): string {
  const lines = md.split("\n");
  const out: string[] = [];

  // Buffer for the current H2 section's body (everything after the heading line
  // up to the next H2 or EOF). We flush + sort on each new H2 and at EOF.
  let sectionBody: string[] = [];
  let inSection = false;

  const flush = () => {
    if (!inSection) {
      out.push(...sectionBody);
      sectionBody = [];
      return;
    }
    // Split section body into blocks separated by one-or-more blank lines.
    // Preserve leading blank lines and trailing blank lines around the group.
    const bodyText = sectionBody.join("\n");
    // Match leading and trailing whitespace to re-attach after sorting.
    const leadMatch = bodyText.match(/^(\s*\n)+/);
    const trailMatch = bodyText.match(/(\n\s*)+$/);
    const lead = leadMatch ? leadMatch[0] : "";
    const trail = trailMatch ? trailMatch[0] : "";
    const core = bodyText.slice(lead.length, bodyText.length - trail.length);

    if (core.length === 0) {
      out.push(...sectionBody);
      sectionBody = [];
      return;
    }

    // Blocks separated by one or more blank lines. Keep the separator uniform
    // ("\n\n") on re-emit since Markdown treats any run of blank lines the same.
    const blocks = core.split(/\n{2,}/);

    const nonEntries: string[] = [];
    const entries: { key: string; block: string }[] = [];

    for (const block of blocks) {
      const trimmed = block.replace(/^\s+/, "");
      const m = trimmed.match(/^\*\*([^*\n]+?)\*\*/);
      if (m) {
        const key = m[1].replace(/\.+$/, "").trim().toLowerCase();
        entries.push({ key, block });
      } else {
        nonEntries.push(block);
      }
    }

    entries.sort((a, b) => a.key.localeCompare(b.key, undefined, { sensitivity: "base" }));

    const reordered = [...nonEntries, ...entries.map((e) => e.block)].join("\n\n");
    out.push(lead + reordered + trail);
    sectionBody = [];
  };

  for (const line of lines) {
    if (/^##\s+/.test(line)) {
      flush();
      out.push(line);
      inSection = true;
      continue;
    }
    if (inSection) {
      sectionBody.push(line);
    } else {
      out.push(line);
    }
  }
  flush();

  return out.join("\n");
}
